import { useMutation } from '@apollo/client/react';
import { Icon, Popover, Toggle } from '@insio/ui-toolkit';
import { AiChatDisplayMode } from '@insio/shared-types';
import { UpdateProfileDocument } from '@/graphql';
import { useAuth } from '@/features/auth';
import { ChatDisplayModePicker } from './ChatDisplayModePicker';
import { useChatDisplayMode } from './use-chat-display-mode';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';

/** Gear button in the chat header opening the per-user chat preferences. */
export function ChatSettingsButton() {
  const t = useFeatureTranslation('aiChat');
  const { showError } = useToastHelpers();
  const { user, refetch } = useAuth();
  const [updateProfile, { loading }] = useMutation(UpdateProfileDocument);
  const displayMode = useChatDisplayMode();

  // Absent means on — the greeting is opt-out, matching the server-side default.
  const greetingEnabled = user?.currentPreferences?.greetingEnabled !== false;

  const savePreferences = (preferences: {
    greetingEnabled?: boolean;
    chatDisplayMode?: AiChatDisplayMode;
  }): void => {
    void updateProfile({ variables: { input: { preferences } } })
      .then(() => refetch?.())
      .catch(() => {
        // Refetch restores the stored value, so the control cannot claim a save that failed.
        showError(t('settings.saveFailed'));
        void refetch?.();
      });
  };

  const setGreetingEnabled = (next: boolean): void => savePreferences({ greetingEnabled: next });

  const setDisplayMode = (next: AiChatDisplayMode): void => {
    if (next !== displayMode) savePreferences({ chatDisplayMode: next });
  };

  return (
    <Popover
      align='bottom end'
      size='lg'
      testId='chat-settings'
      // Owned class, styled in index.css — the panel must clear the AI side panel's stacking layer.
      className='ai-chat-settings-popover'
      triggerButton={
        <button
          type='button'
          aria-label={t('settings.open')}
          title={t('settings.open')}
          className='flex h-[26px] w-[26px] items-center justify-center rounded text-inherit opacity-70 transition-opacity hover:opacity-100'
        >
          <Icon name='gear' className='h-4 w-4' />
        </button>
      }
    >
      <div className='flex flex-col gap-sm p-md'>
        <span className='text-sm font-semibold text-foreground-base'>{t('settings.title')}</span>
        <div className='flex items-start justify-between gap-md'>
          <label
            htmlFor='chat-greeting-toggle'
            className='flex min-w-0 flex-1 flex-col gap-2xs text-sm text-foreground-base'
          >
            {t('settings.greetingLabel')}
            <span className='text-xs text-foreground-muted'>{t('settings.greetingHint')}</span>
          </label>
          {/* The track is a fixed 44px — without shrink-0 the hint text squashes it. */}
          <div className='shrink-0 pt-2xs'>
            <Toggle
              id='chat-greeting-toggle'
              testId='chat-greeting-toggle'
              checked={greetingEnabled}
              disabled={loading}
              onChange={setGreetingEnabled}
            />
          </div>
        </div>
        <div className='flex flex-col gap-2xs'>
          <span className='text-sm text-foreground-base'>{t('settings.displayMode.label')}</span>
          <span className='text-xs text-foreground-muted'>{t('settings.displayMode.hint')}</span>
          <ChatDisplayModePicker value={displayMode} onChange={setDisplayMode} disabled={loading} />
        </div>
      </div>
    </Popover>
  );
}
